/**
 * Hook to handle duplicate chat data from URL parameters
 * Fetches the original chat data and prepares initial form values based on the
 * duplication option.
 *
 * NOTE (issue #124): the retired Order Guide editor is NOT duplicated. There is
 * deliberately no `duplicateOrderGuide` parameter and no `parentChatId`
 * handling — only the ordinary same-buyer / same-distributor field prefill is
 * carried over. See utils/duplicateChatMapper.ts.
 */

import { CreateChatFormData } from '@choco-admin/types';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  buildDuplicateChatData,
  isDuplicateChatOption,
} from '../utils/duplicateChatMapper';

import { useGetChatData } from './useGetChatData';

export interface UseDuplicateChatDataResponse {
  duplicateData: Partial<CreateChatFormData> | null;
  isLoading: boolean;
  error: Error | null;
}

export const useDuplicateChatData = (): UseDuplicateChatDataResponse => {
  const [searchParams] = useSearchParams();
  const [duplicateData, setDuplicateData] =
    useState<Partial<CreateChatFormData> | null>(null);

  const duplicateFromChatId = searchParams.get('duplicateFrom');
  const duplicateOption = searchParams.get('duplicateOption');

  const { chatData, isLoading, error } = useGetChatData(
    duplicateFromChatId || ''
  );

  useEffect(() => {
    // If there's no valid duplicate request, set to empty object (not null)
    if (!duplicateFromChatId || !isDuplicateChatOption(duplicateOption)) {
      setDuplicateData({});
      return;
    }

    // If we're waiting for chat data, don't set anything yet
    if (!chatData && isLoading) {
      return;
    }

    // If we couldn't load the chat data, set to empty object
    if (!chatData) {
      setDuplicateData({});
      return;
    }

    setDuplicateData(buildDuplicateChatData(chatData, duplicateOption));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isLoading omitted intentionally: effect reacts to chatData (the resolved value), not the loading flag itself
  }, [chatData, duplicateOption, duplicateFromChatId]);

  return {
    duplicateData,
    isLoading: isLoading && !!duplicateFromChatId,
    error: duplicateFromChatId ? error : null,
  };
};
